<script>
    
import { buttonStates } from '@repo/config/Global';
    import { onMount, createEventDispatcher } from 'svelte';
    /**
     * @typedef {Object} Props
     * @property {boolean} [disabled]
     * @property {any} [variant]
     * @property {any} title
     * @property {any} [color]
     * @property {string} [size]
     * @property {any} [background]
     * @property {any} [associatedID]
     * @property {any} [rolloverTitle]
     * @property {number} [tabindex]
     * @property {any} [instruction]
     * @property {boolean} [fullWidth]
     * @property {boolean} [pending] Work is in flight: disables the button and
     *   shows a spinner. `title` deliberately stays put — swapping the label
     *   mid-flight renames the control for a screen reader, so `aria-busy` on a
     *   stable name is the signal instead.
     */
    let {
        disabled = false,
        pending = false,
        variant = buttonStates.NEUTRAL,
        title,
        color = null,
        size = 'normal',
        background = null,
        associatedID = null,
        rolloverTitle = null,
        instruction = null,
        fullWidth = false,
        fitContent = false,
        ariaLabel = null,
        ariaExpanded = null,
        ariaControls = null,
        onclick
    } = $props();

    const isDisabled = $derived(disabled || pending);

    let tabindex = $derived.by(() => {
        return isDisabled ? null : 0;
    });

    const dispatch = createEventDispatcher();
    let el = $state(null);

    onMount(() => {
        const elTimer = setInterval(() => {
            if (el) {
                clearInterval(elTimer);
                if (color) el.style.color = color;
                if (background) el.style.backgroundColor = background;
            }
        });
    });

    function handleKeyDown(e) {
        if (isDisabled) return;
        if (e.key === 'Enter') {
            onclick(e);
        }
    }
</script>

<button
    type="button"
    bind:this={el}
    {tabindex}
    {onclick}
    onkeydown={handleKeyDown}
    data-associatedid={associatedID}
    class="button interactive {size} {variant} {isDisabled ? 'disabled' : ''}"
    disabled={isDisabled}
    aria-busy={pending}
    title={rolloverTitle}
    class:full-width={fullWidth}
    class:fit-content={fitContent}
    {...(ariaLabel ? { 'aria-label': ariaLabel } : {})}
    {...(ariaExpanded ? { 'aria-expanded': ariaExpanded } : {})}
    {...(ariaControls ? { 'aria-controls': ariaControls } : {})}
>
    {#if pending}
        <span class="spinner" aria-hidden="true"></span>
    {/if}
    <span>{title}</span>
</button>
{#if instruction}
    <small class="instruction">{instruction}</small>
{/if}

<style>
    .spinner {
        width: 0.85em;
        height: 0.85em;
        margin-right: 0.5rem;
        border: 2px solid currentColor;
        border-right-color: transparent;
        border-radius: 50%;
        flex: none;
        animation: button-spin 0.6s linear infinite;
    }

    @keyframes button-spin {
        to {
            transform: rotate(360deg);
        }
    }

    @media (prefers-reduced-motion: reduce) {
        .spinner {
            animation-duration: 2.4s;
        }
    }

    .button {
        display: flex;
        flex-grow: 0;
        flex-shrink: 0;
        align-items: center;
        justify-content: center;
        padding: 0.75rem 1rem;
        border-radius: 0.5rem;
        width: fit-content;
        min-width: 6rem;
    }

    .button.full-width {
        width: 100%;
    }

    .button.fit-content{
        width:fit-content;
        min-width: unset;
        height: fit-content;
        padding: 0;
    }

    .button.button.fit-content>span{
        padding:0.5rem;
    }

    .button > span {
        display: flex;
        flex-grow: 0;
        flex-shrink: 0;
        align-items: center;
        justify-content: center;
        text-transform: uppercase;
        font-weight: 600;
        font-size: 0.8rem;
    }

    .button.small {
        padding: 0.3rem 0.6rem;
        border-radius: 0.35rem;
    }

    .button.small > span {
        font-size: 0.75rem;
    }

    .button.disabled {
        opacity: 0.25;
    }

    .button:not(.disabled):hover {
        opacity: 0.7;
        box-shadow: 0 0.25rem 0.5rem rgba(0, 0, 0, 0.25);
    }

    .button_state_error {
        background-color: crimson;
        color: white;
    }

    .button_state_warn {
        background-color: rgb(255, 192, 74);
        color: #333;
    }

    .button_state_waiting {
        background-color: rgb(74, 198, 255);
        color: #333;
    }

    .button_state_neutral {
        background-color: rgb(199, 199, 199);
        color: #333;
    }
    .button_state_success {
        background-color: rgb(74, 255, 83);
        color: #333;
    }

    .instruction {
        font-size: 0.75rem;
        font-weight: 400;
        text-transform: none;
        margin: 0.5rem 0 0 0.25rem;
        opacity: 0.5;
    }
</style>
